import type { Statement } from "./types.ts";

/**
 * SID addressing system for NLS documents.
 *
 * SID scheme: top-level children are "1","2","3"...
 * Sub-children alternate number/letter at each depth:
 *   depth 0: root "0"
 *   depth 1: "1", "2", "3" (numbers)
 *   depth 2: "1a", "1b", "2a" (letters)
 *   depth 3: "1a1", "1a2", "1b1" (numbers)
 *   depth 4: "1a1a", "1a1b" (letters)
 *   ...
 */

const LETTERS = "abcdefghijklmnopqrstuvwxyz";

function indexToSegment(index: number, depth: number): string {
  // depth 1 = numbers, depth 2 = letters, depth 3 = numbers, ...
  if (depth % 2 === 1) {
    // numbers (1-based)
    return String(index + 1);
  } else {
    // letters
    return LETTERS[index] ?? String.fromCharCode(97 + index);
  }
}

/**
 * Recursively assign SIDs to all statements in a tree.
 * Mutates the tree in place.
 */
export function generateSIDs(root: Statement): void {
  root.sid = "0";
  assignChildSIDs(root, 1);
}

function assignChildSIDs(node: Statement, depth: number): void {
  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i]!;
    const segment = indexToSegment(i, depth);
    child.sid = node.sid === "0" ? segment : node.sid + segment;
    assignChildSIDs(child, depth + 1);
  }
}

/**
 * Find a statement by SID in the tree.
 */
export function findBySID(
  root: Statement,
  sid: string
): Statement | null {
  if (root.sid === sid) return root;
  for (const child of root.children) {
    const found = findBySID(child, sid);
    if (found) return found;
  }
  return null;
}

/**
 * Get the parent SID from a given SID.
 * "1a1" → "1a", "1a" → "1", "1" → "0", "0" → null
 */
export function parentSID(sid: string): string | null {
  if (sid === "0") return null;
  if (/^\d+$/.test(sid)) return "0"; // top-level: "1" → "0"

  // Walk backward to find where the last segment starts
  // Segments alternate: number-letter-number-letter
  // "1a1" → parent is "1a", "1a1b" → parent is "1a1"
  let i = sid.length - 1;
  const lastChar = sid[i]!;

  if (/[a-z]/.test(lastChar)) {
    // Last segment is a letter: remove it
    return sid.slice(0, i);
  } else {
    // Last segment is a number (possibly multi-digit)
    while (i > 0 && /\d/.test(sid[i - 1]!)) {
      i--;
    }
    return sid.slice(0, i) || "0";
  }
}

/**
 * Parse a SID into its depth level (0-indexed).
 * "0" → 0, "1" → 1, "1a" → 2, "1a1" → 3, "1a1a" → 4
 */
export function sidDepth(sid: string): number {
  if (sid === "0") return 0;

  let depth = 0;
  let i = 0;
  while (i < sid.length) {
    depth++;
    if (/\d/.test(sid[i]!)) {
      // consume number segment
      while (i < sid.length && /\d/.test(sid[i]!)) i++;
    } else {
      // consume letter segment (single letter)
      i++;
    }
  }
  return depth;
}

/**
 * Check if `descendantSid` is a descendant of `ancestorSid`.
 */
export function isDescendantOf(
  descendantSid: string,
  ancestorSid: string
): boolean {
  if (ancestorSid === "0") return true;
  return descendantSid.startsWith(ancestorSid) && descendantSid !== ancestorSid;
}

/**
 * Flatten a statement tree into a list with depth levels.
 */
export function flattenWithDepth(
  root: Statement
): Array<{ statement: Statement; depth: number }> {
  const result: Array<{ statement: Statement; depth: number }> = [];

  function walk(node: Statement, depth: number): void {
    if (node.sid !== "0") {
      result.push({ statement: node, depth });
    }
    for (const child of node.children) {
      walk(child, depth + 1);
    }
  }

  walk(root, 0);
  return result;
}

/**
 * Count all statements in a tree (excluding root).
 */
export function countStatements(root: Statement): number {
  let count = 0;
  function walk(node: Statement): void {
    if (node.sid !== "0") count++;
    for (const child of node.children) walk(child);
  }
  walk(root);
  return count;
}

/**
 * Get the maximum depth of the tree.
 */
export function maxDepth(root: Statement): number {
  let max = 0;
  function walk(node: Statement, depth: number): void {
    if (depth > max) max = depth;
    for (const child of node.children) walk(child, depth + 1);
  }
  walk(root, 0);
  return max;
}

/**
 * Count all links in a tree.
 */
export function countLinks(root: Statement): number {
  let count = 0;
  function walk(node: Statement): void {
    count += node.links.length;
    for (const child of node.children) walk(child);
  }
  walk(root);
  return count;
}
